import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Chip,
  CircularProgress,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import moment from 'moment'
import { fetchData } from '../../../../../../global/utils/fetch'
import { getSchedulePatternHistory } from '../api'

// Define the interface for schedule patterns from backend
interface SchedulePatternHistoryEntry {
  objectId: string
  weekPatterns: Array<{ week: number; days: string[] }>
  effectiveFrom: Date | string
  reason?: string
  updatedBy?: string
  workDayFrom?: string
  workDayTo?: string
  lunchDuration?: number
  created: Date | string
}

interface SchedulePatternHistoryModalProps {
  open: boolean
  onClose: () => void
  technicianObjectId: string
  technicianName: string
}

export default function SchedulePatternHistoryModal({
  open,
  onClose,
  technicianObjectId,
  technicianName,
}: SchedulePatternHistoryModalProps) {
  const [scheduleHistory, setScheduleHistory] = useState<SchedulePatternHistoryEntry[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fetch schedule pattern history when modal opens
  useEffect(() => {
    if (!open || !technicianObjectId) return

    const fetchScheduleHistory = async () => {
      setLoading(true)
      setError(null)
      try {
        const apiCall = getSchedulePatternHistory(technicianObjectId)
        const response = await fetchData<SchedulePatternHistoryEntry[]>(apiCall)
        setScheduleHistory(response)
      } catch (err) {
        setError('Failed to load schedule pattern history')
        console.error('Error fetching schedule pattern history:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchScheduleHistory()
  }, [open, technicianObjectId])

  const renderScheduleGrid = (weekPatterns: Array<{ week: number; days: string[] }>) => {
    if (!weekPatterns || weekPatterns.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary" style={{ fontStyle: 'italic' }}>
          No schedule pattern defined
        </Typography>
      )
    }

    // Sort weeks by week number
    const sortedWeeks = [...weekPatterns].sort((a, b) => a.week - b.week)

    return (
      <Box>
        {sortedWeeks.map((week, idx) => (
          <Box key={idx} display="flex" alignItems="center" gap={1} mb={0.5}>
            <Typography variant="caption" sx={{ minWidth: '24px', fontWeight: 500 }}>
              W{week.week + 1}
            </Typography>
            {['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].map((day) => (
              <Box
                key={day}
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '3px',
                  backgroundColor: week.days.includes(day) ? '#2e7d32' : '#f5f5f5',
                  border: '1px solid #e0e0e0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.6rem',
                  fontWeight: 600,
                  color: week.days.includes(day) ? 'white' : '#999',
                }}
                title={`${day}: ${week.days.includes(day) ? 'Working' : 'Off'}`}
              >
                {day.slice(0, 1).toUpperCase()}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    )
  }

  const renderWorkTimes = (pattern: SchedulePatternHistoryEntry) => {
    const hasWorkTimes = pattern.workDayFrom || pattern.workDayTo || pattern.lunchDuration

    if (!hasWorkTimes) {
      return (
        <Typography variant="body2" color="textSecondary" style={{ fontStyle: 'italic' }}>
          Using location defaults
        </Typography>
      )
    }

    return (
      <Box display="flex" flexDirection="column" gap={0.5}>
        {pattern.workDayFrom && (
          <Typography variant="body2">
            <span style={{ fontWeight: 500 }}>Start:</span> {pattern.workDayFrom}
          </Typography>
        )}
        {pattern.workDayTo && (
          <Typography variant="body2">
            <span style={{ fontWeight: 500 }}>End:</span> {pattern.workDayTo}
          </Typography>
        )}
        {pattern.lunchDuration && (
          <Typography variant="body2">
            <span style={{ fontWeight: 500 }}>Lunch:</span> {pattern.lunchDuration} min
          </Typography>
        )}
      </Box>
    )
  }

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Schedule Pattern History - {technicianName}
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" p={3}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    )
  }

  if (error) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Schedule Pattern History - {technicianName}
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent>
          <Box p={3} textAlign="center" color="error.main">
            <Typography>{error}</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    )
  }

  if (!scheduleHistory || scheduleHistory.length === 0) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Schedule Pattern History - {technicianName}
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent>
          <Box p={3} textAlign="center" color="textSecondary">
            <Typography>No schedule pattern history found for this technician.</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Schedule Pattern History - {technicianName}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>
        <Typography variant="subtitle2" color="textSecondary">
          Most recent first
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box 
          position="relative"
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            backgroundColor: '#fafafa',
            maxHeight: '500px',
            overflowY: 'auto',
            p: 2,
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#c1c1c1',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: '#a8a8a8',
            }
          }}
        >
          {/* Visual timeline indicator */}
          <Box
            position="absolute"
            left={4}
            top={16}
            bottom={16}
            width={4}
            sx={{
              background: 'linear-gradient(to bottom, #2e7d32 0%, #4caf50 30%, #81c784 70%, #c8e6c9 100%)',
              borderRadius: 2
            }}
          />
          
          {scheduleHistory.map((pattern, index) => (
            <Box
              key={pattern.objectId}
              p={2}
              mb={1.5}
              ml={2}
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                backgroundColor: index === 0 ? '#f8f9fa' : '#ffffff',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: -18,
                  top: 0,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: index === 0 ? '#2e7d32' : (index === scheduleHistory.length - 1 ? '#c8e6c9' : '#4caf50'),
                  border: '2px solid white',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }
              }}
            >
              {/* Header with dates and badges */}
              <Box display="flex" justifyContent="flex-start" alignItems="center" mb={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip
                    label={moment(pattern.created).format('MMM D, YYYY [at] h:mm A')}
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                  />
                  <Chip
                    label={`Effective: ${moment(pattern.effectiveFrom).format('MMM D, YYYY')}`}
                    size="small"
                    sx={{ 
                      backgroundColor: '#e3f2fd', 
                      color: '#1976d2',
                      fontWeight: 500,
                      fontSize: '0.7rem'
                    }}
                  />
                  {index === 0 && (
                    <Chip
                      label="CURRENT"
                      size="small"
                      sx={{ 
                        backgroundColor: '#e8f5e8', 
                        color: '#2e7d32',
                        fontWeight: 600,
                        fontSize: '0.7rem'
                      }}
                    />
                  )}
                </Box>
              </Box>

              {/* Two-column layout: Schedule Grid and Work Times */}
              <Box display="flex" gap={3} mb={2}>
                {/* Left column: Schedule Grid */}
                <Box flex={1}>
                  <Typography variant="caption" color="textSecondary" display="block" gutterBottom fontWeight={500}>
                    Schedule Pattern:
                  </Typography>
                  {renderScheduleGrid(pattern.weekPatterns)}
                </Box>

                {/* Right column: Work Times */}
                <Box flex={1}>
                  <Typography variant="caption" color="textSecondary" display="block" gutterBottom fontWeight={500}>
                    Work Times:
                  </Typography>
                  {renderWorkTimes(pattern)}
                </Box>
              </Box>

              {/* Full-width row: Reason */}
              {pattern.reason && (
                <Box>
                  <Typography variant="caption" color="textSecondary" display="block" gutterBottom fontWeight={500}>
                    Reason:
                  </Typography>
                  <Box 
                    p={1.5} 
                    sx={{ 
                      backgroundColor: '#f8f9fa', 
                      border: '1px solid #e9ecef', 
                      borderRadius: 1,
                      fontStyle: 'italic'
                    }}
                  >
                    <Typography variant="body2" color="textPrimary">
                      "{pattern.reason}"
                    </Typography>
                  </Box>
                  {pattern.updatedBy && (
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block', fontStyle: 'normal' }}>
                      by {pattern.updatedBy}
                    </Typography>
                  )}
                </Box>
              )}
              
              {/* Show user even if no reason (fallback) */}
              {!pattern.reason && pattern.updatedBy && (
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    by {pattern.updatedBy}
                  </Typography>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}